import React, { useCallback, useMemo, useId } from 'react';
import { createUseStyles } from 'react-jss';
import { cssSpacingSm } from '../constants/globalcss';
import FieldLabel from './FieldLabel';
import MultiSelect from './MultiSelect';
import { ILabel, ILabelOption } from '../constants/interfaces';

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: cssSpacingSm,
    width: '100%',
  },
  fieldContainer: {
    width: '100%',
  },
  label: {
    marginBottom: cssSpacingSm,
  },
});

/**
 * Props for the MultiSelectField component
 */
export interface IMultiSelectFieldProps {
  /** Field configuration containing label text, options, validation rules, etc. */
  field: ILabel;
  /** Current selected values as semicolon-separated string */
  value: string;
  /** Callback fired when the selection changes */
  onChange: (value: string, children?: unknown[]) => void;
  /** Error message to display */
  errorText?: string;
  /** Whether the field is read-only/display only */
  displayOnly?: boolean;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Width of the multi-select */
  width?: string;
  /** Custom placeholder text */
  placeholder?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Maximum number of selections allowed */
  maxSelections?: number;
  /** Whether to show selection count */
  showSelectionCount?: boolean;
  /** Callback fired when dropdown gains focus */
  onFocus?: () => void;
  /** Callback fired when dropdown loses focus */
  onBlur?: () => void;
}

/**
 * Utility functions for multi-select handling
 */
const multiSelectUtils = {
  /**
   * Converts field options to MultiSelect format
   */
  convertToMultiSelectOptions: (options: ILabelOption[]) => {
    return options.map((option) => ({
      id: option.labelOptionId,
      label: option.text,
    }));
  },

  /**
   * Gets display text for selected items
   */
  getDisplayText: (selectedIds: string[], options: ILabelOption[]): string => {
    const selectedOptions = options.filter((option) =>
      selectedIds.includes(option.labelOptionId.toString())
    );
    return selectedOptions.map((option) => option.text).join(', ');
  },

  /**
   * Gets selection count text
   */
  getSelectionCountText: (count: number, maxSelections?: number): string => {
    if (maxSelections) {
      return `${count}/${maxSelections} selected`;
    }
    return `${count} selected`;
  },

  /**
   * Converts semicolon-separated string to array of IDs
   */
  parseValue: (value: string): string[] => {
    return value ? value.split(';').filter(Boolean) : [];
  },

  /**
   * Converts array of IDs to semicolon-separated string
   */
  stringifyValue: (values: (string | number)[]): string => {
    return values.filter(Boolean).join(';');
  },

  /**
   * Validates multi-select value based on field requirements
   */
  validateMultiSelect: (
    value: string,
    field: ILabel,
    maxSelections?: number
  ): string | null => {
    const selectedCount = value ? value.split(';').filter(Boolean).length : 0;

    if (field.isMandatory && selectedCount === 0) {
      return `${field.text} is required`;
    }

    if (maxSelections && selectedCount > maxSelections) {
      return `${field.text} can have at most ${maxSelections} selections`;
    }

    return null;
  },
};

/**
 * MultiSelectField Component
 *
 * A reusable multi-select component that integrates with form validation
 * and provides a consistent interface for multiple option selection across the application.
 *
 * Features:
 * - Multiple option selection with validation
 * - Dynamic option loading from field configuration
 * - Configurable maximum selections
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Selection count display
 * - Memoized performance optimizations
 *
 * @example
 * ```tsx
 * <MultiSelectField
 *   value="1;3;5"
 *   onChange={handleSelectionChange}
 *   field={categoriesField}
 *   errorText={validationError}
 *   placeholder="Select categories"
 *   maxSelections={3}
 * />
 * ```
 */
const MultiSelectField: React.FC<IMultiSelectFieldProps> = ({
  field,
  value,
  onChange,
  errorText,
  displayOnly = false,
  isLabelHidden = false,
  width,
  placeholder,
  disabled = false,
  maxSelections,
  showSelectionCount = false,
  onFocus,
  onBlur,
}) => {
  const classes = useStyles();

  // Generate unique ID for accessibility
  const fieldId = useId();

  // Memoize computed values
  const computedValues = useMemo(() => {
    const options = field.options || [];
    const multiSelectOptions =
      multiSelectUtils.convertToMultiSelectOptions(options);
    const selectedIds = multiSelectUtils.parseValue(value);
    const selectionCount = selectedIds.length;
    const placeholderText = placeholder || 'Please select all that apply';
    const hasError = Boolean(errorText);
    const isDisabled = disabled || displayOnly;
    const displayText = multiSelectUtils.getDisplayText(selectedIds, options);
    const selectionCountText = multiSelectUtils.getSelectionCountText(
      selectionCount,
      maxSelections
    );

    return {
      displayText,
      hasError,
      isDisabled,
      multiSelectOptions,
      options,
      placeholderText,
      selectedIds,
      selectionCount,
      selectionCountText,
    };
  }, [
    field.options,
    field.text,
    value,
    placeholder,
    errorText,
    disabled,
    displayOnly,
    maxSelections,
  ]);

  // Optimized change handler with useCallback
  const handleChange = useCallback(
    (newValues: (string | number)[]) => {
      // Enforce max selections if specified
      if (maxSelections && newValues.length > maxSelections) {
        return; // Don't allow more than max selections
      }

      const stringValue = multiSelectUtils.stringifyValue(newValues);
      onChange(stringValue);
    },
    [onChange, maxSelections]
  );

  // Optimized focus handler (currently unused but kept for future enhancement)
  // const handleFocus = useCallback(() => {
  //   onFocus?.();
  // }, [onFocus]);

  // Optimized blur handler (currently unused but kept for future enhancement)
  // const handleBlur = useCallback(() => {
  //   onBlur?.();
  // }, [onBlur]);

  // Generate helper text for accessibility
  const helperText = useMemo(() => {
    if (errorText) return errorText;
    if (showSelectionCount) return computedValues.selectionCountText;
    return '';
  }, [errorText, showSelectionCount, computedValues.selectionCountText]);

  return (
    <div className={classes.container}>
      {/* Label with proper accessibility attributes */}
      {!isLabelHidden && (
        <div className={classes.label}>
          <FieldLabel field={field} htmlFor={fieldId} noHover noBorder />
        </div>
      )}

      {/* Multi-select with enhanced props */}
      <div className={classes.fieldContainer}>
        <MultiSelect
          field={field}
          options={computedValues.multiSelectOptions}
          value={computedValues.selectedIds}
          onChange={handleChange}
          placeholder={computedValues.placeholderText}
          error={computedValues.hasError}
          helperText={helperText}
          isLabelHidden={true}
          disabled={computedValues.isDisabled}
          maxSelections={maxSelections}
        />
      </div>
    </div>
  );
};

export default MultiSelectField;

// Export utilities for testing and external use
export { multiSelectUtils };
